import {
  DOWNLOAD_PDF_PROFILE,
  fitWithin,
  PdfRenderProfile,
} from './pdfRenderProfile'
import { waitForReportImage } from './waitForReportImage'
import * as r from '../../react-utils'

import { saveAs } from 'file-saver'
import html2canvas from 'html2canvas'
import { PDFDocument } from 'pdf-lib'

// Inline every non-data-URI <img> as a base64 data URI before capture.
//
// Root cause (found live via devtools, 2026-07-25, Maria G report): with
// `foreignObjectRendering: true` (the Chrome/Edge path — see `safari` below),
// html2canvas serializes the DOM subtree to an SVG data URI and has the
// browser rasterize it natively. That path does its OWN independent resource
// fetch for any network-backed <img src> — separate from, and not awaited
// by, html2canvas's returned promise — even when the DOM <img> element
// itself already finished loading. Confirmed via Network panel: capturing
// SalesSlick.tsx's ~90KB PNG (across the 7-page export loop) fired 7 separate
// GETs for the same URL, several returning 304 (revalidated from disk cache,
// still a network round-trip) — and that round-trip visibly wasn't awaited
// before the page rasterized, since it kept baking in the browser's
// broken-image glyph + alt text ("HomeCam vs. traditional providers") on an
// otherwise-blank page. A first attempt just waited for the DOM <img>'s own
// `load` event before capture — didn't help, because that image was already
// `complete` by then; the race is entirely inside html2canvas's foreignObject
// path, invisible to the DOM img's load state.
// A data: URI has no network dependency at rasterization time, which
// sidesteps the race regardless of which html2canvas renderer is used.
// Originals are restored after capture, same pattern as the Safari
// input/underline swaps below.
async function inlinePageImagesAsDataURIs(
  pageElements: readonly HTMLElement[],
  profile: PdfRenderProfile,
): Promise<{ img: HTMLImageElement; origSrc: string }[]> {
  const images = pageElements.flatMap((el) =>
    Array.from(el.querySelectorAll<HTMLImageElement>('img')),
  )
  const swapped: { img: HTMLImageElement; origSrc: string }[] = []
  await Promise.all(
    images
      .filter((img) => !img.src.startsWith('data:'))
      .map((img) => waitForReportImage(img)),
  )
  for (const img of images) {
    if (img.src.startsWith('data:')) continue
    if (!img.complete || img.naturalWidth === 0) continue
    // Customer photos (MediaGridReport marks them with data-fancybox) may be
    // downscaled for the email profile; logos and other artwork never are.
    const photo = img.hasAttribute('data-fancybox')
    const size = photo
      ? fitWithin(img.naturalWidth, img.naturalHeight, profile.maxPhotoSide)
      : { width: img.naturalWidth, height: img.naturalHeight }
    const canvas = document.createElement('canvas')
    canvas.width = size.width
    canvas.height = size.height
    const ctx = canvas.getContext('2d')
    if (!ctx) continue
    try {
      ctx.drawImage(img, 0, 0, size.width, size.height)
      const dataUrl =
        photo && profile.photoInlineJpegQuality !== undefined
          ? canvas.toDataURL('image/jpeg', profile.photoInlineJpegQuality)
          : canvas.toDataURL('image/png')
      swapped.push({ img, origSrc: img.src })
      img.src = dataUrl
    } catch (e) {
      // Canvas draw/export failed — almost always a tainted canvas
      // (cross-origin image loaded without crossOrigin="anonymous", or the
      // bucket's CORS policy not matching this origin). Leave the original
      // <img src> in place rather than lose the image entirely; it will
      // render as a broken-image glyph in the PDF. This swallowed silently
      // for weeks (B137) — name the URL so the next one is visible.
      console.error('[downloadReportAsPdf] could not inline image:', img.src, e)
    }
  }
  return swapped
}

export async function generatePdfBytes(
  customerName: string,
  options: { renderer?: 'auto' | 'canvas'; profile?: PdfRenderProfile } = {},
): Promise<{ bytes: Uint8Array; fileName: string } | null> {
  const pageElements = document.querySelectorAll<HTMLElement>('[data-pdf-page]')
  if (pageElements.length === 0) return null
  const profile = options.profile ?? DOWNLOAD_PDF_PROFILE

  const swappedImages = await inlinePageImagesAsDataURIs(
    Array.from(pageElements),
    profile,
  )
  await Promise.all(
    swappedImages.map(({ img }) =>
      img.complete
        ? Promise.resolve()
        : new Promise<void>((resolve) => {
            img.addEventListener('load', () => resolve(), { once: true })
            img.addEventListener('error', () => resolve(), { once: true })
          }),
    ),
  )

  const now = new Date()
  const date = now
    .toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: '2-digit',
    })
    .replace(/,?\s/g, '-')
  const time = now
    .toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
    .replace(/:/g, '-')
  const fileName = `${customerName}_${date}_${time}.pdf`

  // Explicit canvas capture is used by the guided email flow after local PDF QA.
  // Both contract delivery and manual downloads use the verified canvas renderer.
  const safari = options.renderer === 'canvas' || r.isSafari()

  // Safari's html2canvas standard renderer (foreignObject disabled) has two
  // quirks that require inline style patches before capture:
  // 1) Underlines render too close to text — html2canvas doesn't support
  //    text-underline-offset, so we swap underlines for border-bottom and add
  //    padding to create spacing.
  // 2) MUI input underline pseudo-elements have an opaque background that
  //    produces a white bar clipping over the text — we force input elements
  //    to have transparent backgrounds.
  // Safari's html2canvas standard renderer (foreignObject disabled) has two
  // quirks:
  // 1) Underlines render too close to text — html2canvas doesn't support
  //    text-underline-offset, so we swap underlines for border-bottom with
  //    padding to create spacing.
  // 2) MUI inputs render a white bar that clips over the text — no CSS fix
  //    works reliably, so we swap <input> elements for <span> elements with
  //    matching styles and restore them after capture.
  const safariStylePatched: { el: HTMLElement; orig: string }[] = []
  const safariInputSwapped: {
    span: HTMLSpanElement
    input: HTMLInputElement
    parent: Node
  }[] = []
  const pdfDoc = await PDFDocument.create()
  try {
    if (safari) {
      document
        .querySelectorAll<HTMLInputElement>('[data-pdf-page] input')
        .forEach((input) => {
          const cs = getComputedStyle(input)
          const span = document.createElement('span')
          span.textContent = input.value
          span.style.cssText = `
        font: ${cs.font};
        color: ${cs.color};
        letter-spacing: ${cs.letterSpacing};
        padding: ${cs.padding};
        border: none;
        border-bottom: ${cs.borderBottom};
        display: inline-block;
        width: ${cs.width};
        box-sizing: ${cs.boxSizing};
        background: transparent;
      `
          if (input.parentNode) {
            safariInputSwapped.push({ span, input, parent: input.parentNode })
            input.parentNode.replaceChild(span, input)
          }
        })

      document
        .querySelectorAll<HTMLElement>('[data-pdf-page] *')
        .forEach((el) => {
          const cs = getComputedStyle(el)
          if (!cs.textDecorationLine.includes('underline')) return
          safariStylePatched.push({ el, orig: el.style.cssText })
          el.style.textDecoration = 'none'
          el.style.borderBottom = `1px solid ${cs.color}`
          el.style.paddingBottom = '2px'
        })
    }

    for (const el of Array.from(pageElements)) {
      const w = el.scrollWidth
      const h = el.scrollHeight
      if (w === 0 || h === 0) continue
      const photoPage = !!el.querySelector('img[data-fancybox]')

      const canvas = await html2canvas(el, {
        backgroundColor: '#FFFFFF',
        foreignObjectRendering: !safari,
        scale: photoPage ? profile.photoPageScale : profile.scale,
        scrollX: 0,
        scrollY: 0,
        useCORS: true,
        width: w,
        height: h,
      })

      if (canvas.width === 0 || canvas.height === 0) continue

      const dataUrl = canvas.toDataURL(
        'image/jpeg',
        photoPage ? profile.photoPageJpegQuality : profile.jpegQuality,
      )
      const imgBytes = Uint8Array.from(atob(dataUrl.split(',')[1]!), (c) =>
        c.charCodeAt(0),
      )

      const image = await pdfDoc.embedJpg(imgBytes)
      const { width, height } = image.scale(1)

      const pdfPageWidth = 595.28
      const scaleFactor = pdfPageWidth / width
      const pdfPageHeight = height * scaleFactor

      const page = pdfDoc.addPage([pdfPageWidth, pdfPageHeight])
      page.drawImage(image, {
        x: 0,
        y: 0,
        width: pdfPageWidth,
        height: pdfPageHeight,
      })
    }
  } finally {
    for (const { el, orig } of safariStylePatched) el.style.cssText = orig
    for (const { span, input, parent } of safariInputSwapped) {
      if (span.parentNode === parent) parent.replaceChild(input, span)
    }
    for (const { img, origSrc } of swappedImages) img.src = origSrc
  }
  if (pdfDoc.getPageCount() === 0)
    throw new Error('The contract could not be rendered. Please try again.')
  const bytes = await pdfDoc.save()
  return { bytes, fileName }
}

export default async function downloadReportAsPdf(
  customerName: string,
): Promise<void> {
  const result = await generatePdfBytes(customerName, { renderer: 'canvas' })
  if (!result) return
  const { bytes, fileName } = result
  saveAs(
    new Blob([bytes.buffer as ArrayBuffer], { type: 'application/pdf' }),
    fileName,
  )
}
